import { useState } from 'react';
import PropTypes from 'prop-types';
import { NavLink as RouterLink, matchPath, useLocation } from 'react-router-dom';
import Iconify from './Iconify';
import { useHelp } from '../contexts/HelpContext';
import { Tooltip } from '@mui/material';

// Animation CSS pour la pulsation
const pulseAnimation = `
  @keyframes pulse {
    0%, 100% {
      opacity: 1;
    }
    50% {
      opacity: 0.7;
    }
  }
  
  /* S'assurer que les styles inline ont la priorité */
  .active[style] {
    background-color: inherit !important;
    color: inherit !important;
  }
`;

// ----------------------------------------------------------------------

NavItem.propTypes = {
  item: PropTypes.object,
  active: PropTypes.func,
};

function NavItem({ item, active, onLinkClick, index }) {
  const isActiveRoot = active(item.path);
  const { title, path, icon, info, children } = item;
  const [open, setOpen] = useState(isActiveRoot);
  const { isHelpMode, activeTooltip, nextTooltip } = useHelp();

  // Définir les tooltips pour chaque menu
  const getTooltipContent = (title) => {
    const tooltips = {
      'Tableau de bord': 'Vue d\'ensemble de vos statistiques et accès rapides',
      'Publier': 'Créer une annonce pour un objet trouvé',
      'Objets Trouvés': 'Gérer tous vos objets trouvés et les demandes de restitution',
      'Objets Restitués': 'Consulter les objets rendus à leurs propriétaires',
      'Objets à expédier': 'Objets prêts à être envoyés par la poste',
      'Objets partis en livraison': 'Objets expédiés et en cours de livraison',
      'Messages': 'Conversations avec les festivaliers et notifications',
      'Stockages': 'Organiser les objets dans des boîtes par catégorie'
    };
    return tooltips[title] || title;
  };

  const isTooltipActive = isHelpMode && activeTooltip === index;

  const handleOpen = () => {
    if (isHelpMode) {
      nextTooltip();
      // Empêcher l'ouverture du sous-menu en mode aide
      return;
    } else {
      setOpen((prev) => !prev);
    }
  };

  const getActiveRootStyles = () => {
    if (isTooltipActive) {
      return {
        color: 'white',
        fontWeight: '500',
        backgroundColor: '#FF9800',
        boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
        animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite'
      };
    } else if (isActiveRoot) {
      return {
        color: '#FFFFFF',
        fontWeight: '600',
        backgroundColor: '#1976d2',
        boxShadow: '0 4px 8px rgba(25, 118, 210, 0.3)',
        borderLeft: '4px solid #1565c0'
      };
    } else {
      return {
        color: '#6B7280',
        fontWeight: '400'
      };
    }
  };

  const activeSubClasses = isActiveRoot
    ? 'text-gray-900 font-medium'
    : 'text-gray-600 hover:bg-gray-100';

  if (children) {
    const buttonContent = (
      <button
        onClick={handleOpen}
        className="w-full h-12 px-3 flex items-center rounded-md transition-colors hover:bg-gray-100"
        style={{
          ...getActiveRootStyles(),
          ...(isTooltipActive && {
            outline: '2px solid #FB8C00',
            outlineOffset: '2px'
          })
        }}
      >
        <span className="w-[22px] h-[22px] flex items-center justify-center text-inherit">
          {icon}
        </span>
        <span className="ml-3 flex-grow text-left">{title}</span>
        {info}
        <Iconify
          icon={open ? 'eva:arrow-ios-downward-fill' : 'eva:arrow-ios-forward-fill'}
          className="w-4 h-4 ml-1"
        />
      </button>
    );

    return (
      <>
        {isTooltipActive ? (
          <Tooltip
            title={getTooltipContent(title)}
            placement="right"
            arrow
            open={true}
            PopperProps={{
              sx: {
                '& .MuiTooltip-tooltip': {
                  backgroundColor: '#FF9800',
                  color: 'white',
                  fontSize: '0.875rem',
                  padding: '8px 12px',
                  maxWidth: 250,
                  boxShadow: '0 4px 12px rgba(255, 152, 0, 0.3)',
                },
                '& .MuiTooltip-arrow': {
                  color: '#FF9800',
                },
              },
            }}
          >
            {buttonContent}
          </Tooltip>
        ) : (
          buttonContent
        )}

        <div className={`overflow-hidden transition-all duration-200 ${open ? 'max-h-96' : 'max-h-0'}`}>
          <div className="pl-4">
            {children.map((item) => {
              const { title, path } = item;
              const isActiveSub = active(path);

              return (
                <RouterLink
                  key={title}
                  to={path}
                  className={`w-full h-12 px-3 flex items-center rounded-md transition-colors ${activeSubClasses}`}
                  onClick={onLinkClick}
                >
                  <span className="w-[22px] h-[22px] flex items-center justify-center">
                    <span
                      className={`w-1 h-1 rounded-full bg-gray-400 transition-transform ${
                        isActiveSub ? 'scale-200 bg-primary-main' : ''
                      }`}
                    />
                  </span>
                  <span className="ml-3 flex-grow text-left">{title}</span>
                </RouterLink>
              );
            })}
          </div>
        </div>
      </>
    );
  }

  const handleLinkClick = (e) => {
    if (isHelpMode) {
      e.preventDefault();
      nextTooltip();
      return;
    } else if (onLinkClick) {
      onLinkClick();
    }
  };

  const linkContent = (
    <RouterLink
      to={path}
      className={`w-full h-12 px-3 flex items-center rounded-md transition-colors hover:bg-gray-100 ${
        isTooltipActive ? '' : (isActiveRoot ? 'active' : '')
      }`}
      style={{
        ...getActiveRootStyles(),
        ...(isTooltipActive && {
          outline: '2px solid #FB8C00',
          outlineOffset: '2px'
        })
      }}
      onClick={handleLinkClick}
    >
      <span className="w-[22px] h-[22px] flex items-center justify-center text-inherit">
        {icon}
      </span>
      <span className="ml-3 flex-grow text-left">{title}</span>
      {info}
    </RouterLink>
  );

        return (
        <>
          {isTooltipActive ? (
            <Tooltip
              title={getTooltipContent(title)}
              placement="right"
              arrow
              open={true}
              PopperProps={{
                sx: {
                  '& .MuiTooltip-tooltip': {
                    backgroundColor: '#FF9800',
                    color: 'white',
                    fontSize: '0.875rem',
                    padding: '8px 12px',
                    maxWidth: 250,
                    boxShadow: '0 4px 12px rgba(255, 152, 0, 0.3)',
                  },
                  '& .MuiTooltip-arrow': {
                    color: '#FF9800',
                  },
                },
              }}
            >
              {linkContent}
            </Tooltip>
          ) : (
            linkContent
          )}
        </>
      );
}

NavSection.propTypes = {
  navConfig: PropTypes.array,
};

export default function NavSection({ navConfig, onLinkClick, ...other }) {
  const { pathname } = useLocation();

  const match = (path) => {
    if (!path) return false;
    
    // Pour le tableau de bord, faire une correspondance exacte
    if (path === '/dashboard') {
      return pathname === '/dashboard';
    }
    
    // Pour les autres chemins, faire une correspondance partielle
    return !!matchPath({ path, end: false }, pathname);
  };

  return (
    <div {...other}>
      <style>{pulseAnimation}</style>
      <nav className="p-1">
        {navConfig.map((item, index) => (
          <NavItem key={item.title} item={item} active={match} onLinkClick={onLinkClick} index={index} />
        ))}
      </nav>
    </div>
  );
}
